import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Icons from 'lucide-react';
import { services } from '../mock';
import ContactForm from '../components/ContactForm';

const ServicesPage = () => {
  const { t } = useTranslation();

  const getIconComponent = (iconName) => {
    const IconComponent = Icons[iconName.split('-').map((word, index) => 
      index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join('').replace(/-/g, '')];
    return IconComponent || Icons.Package;
  };

  return (
    <div style={{ background: 'var(--bg-page)', paddingTop: '80px' }}>
      {/* Hero Section */}
      <section
        style={{
          background: '#0A1628',
          padding: '5rem 1.2rem',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '50vh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              linear-gradient(rgba(34, 197, 94, 0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(34, 197, 94, 0.03) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            zIndex: 0,
          }}
        />

        <div
          style={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle, rgba(34, 197, 94, 0.15) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            zIndex: 0,
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '10%',
            right: '15%',
            width: '250px',
            height: '250px',
            background: 'radial-gradient(circle, rgba(245, 158, 11, 0.15) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            zIndex: 0,
          }}
        />

        <div style={{ position: 'absolute', top: '15%', left: '5%', opacity: 0.2, zIndex: 1 }}>
          <Icons.Stethoscope size={60} color="#22c55e" />
        </div>
        <div style={{ position: 'absolute', top: '25%', right: '8%', opacity: 0.2, zIndex: 1 }}>
          <Icons.Brain size={50} color="#22c55e" />
        </div>
        <div style={{ position: 'absolute', bottom: '20%', left: '12%', opacity: 0.2, zIndex: 1 }}>
          <Icons.Leaf size={45} color="#f59e0b" />
        </div>
        <div style={{ position: 'absolute', bottom: '30%', right: '10%', opacity: 0.2, zIndex: 1 }}>
          <Icons.Heart size={40} color="#ec4899" />
        </div>

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'rgba(34, 197, 94, 0.2)',
              borderRadius: '2rem',
              padding: '0.5rem 1.5rem',
              marginBottom: '2rem',
              border: '1px solid rgba(34, 197, 94, 0.4)',
            }}
          >
            <Icons.Sparkles size={18} color="#22c55e" />
            <span style={{ color: '#22c55e', fontWeight: '600', fontSize: '0.9rem' }}>
              {t('servicesPage.badge')}
            </span>
          </div>

          <h1 
            className="heading-hero" 
            style={{ 
              marginBottom: '1.5rem',
              background: 'linear-gradient(135deg, #ffffff 0%, #22c55e 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {t('servicesPage.title')}
          </h1>
          
          <p
            className="body-large"
            style={{
              maxWidth: '800px',
              margin: '0 auto 2rem',
              color: 'rgba(255, 255, 255, 0.8)',
              lineHeight: '1.8',
              fontSize: '1.2rem',
            }}
          >
            {t('servicesPage.description')}
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section
        style={{
          background: '#0A1628',
          padding: '5rem 1.2rem',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '40%',
            height: '100%',
            backgroundImage: 'url(https://images.unsplash.com/photo-1730977806288-96b82f795008?q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.25,
            zIndex: 0,
          }}
        />
        
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            background: 'radial-gradient(circle at 80% 50%, rgba(6, 182, 212, 0.15) 0%, transparent 50%)',
            zIndex: 0,
          }}
        />
        
        <div className="data-grid-bg" />

        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem', position: 'relative', zIndex: 1 }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem', color: 'var(--text-light)' }}>
              {t('servicesPage.whatWeOffer')}
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
              {t('servicesPage.whatWeOfferDesc')}
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '2rem',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {services.map((service, index) => {
              const IconComponent = getIconComponent(service.icon);
              const serviceIndex = index + 1;

              return (
                <div
                  key={service.id}
                  className={`service-card ${service.accentColor}`}
                >
                  <div
                    className={`icon-container-${service.iconType}`}
                    style={{
                      width: '3.5rem',
                      height: '3.5rem',
                      borderRadius: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginTop: '1.25rem',
                      marginBottom: '1.25rem',
                    }}
                  >
                    <IconComponent size={28} color="white" />
                  </div>

                  <h3 className="heading-3" style={{ marginBottom: '0.75rem', color: 'var(--tech-green-300)' }}>
                    {t(`servicesPage.service${serviceIndex}.title`)}
                  </h3>
                  <p className="body-small" style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
                    {t(`servicesPage.service${serviceIndex}.description`)}
                  </p>

                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {[1, 2, 3, 4].map((featureNum) => (
                      <li
                        key={featureNum}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          marginBottom: '0.5rem',
                        }}
                      >
                        <Icons.Check size={16} color="var(--tech-green-400)" />
                        <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          {t(`servicesPage.service${serviceIndex}.feature${featureNum}`)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        style={{
          padding: '4rem 1.2rem',
          background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
          textAlign: 'center',
        }}
      >
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
            {t('servicesPage.ctaTitle')}
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '2rem', maxWidth: '600px', margin: '0 auto 2rem' }}>
            {t('servicesPage.ctaDesc')}
          </p>
          <button
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="btn-primary hover-scale"
            style={{ padding: '1rem 2.5rem', fontSize: '1rem' }}
          >
            {t('servicesPage.ctaButton')}
          </button>
        </div>
      </section>

      <ContactForm />
    </div>
  );
};

export default ServicesPage;
